import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Map as ImmutableMap } from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UISelect from 'UIComponents/input/UISelect';
import { getDisplayOrderFromPipeline, getHsPipelineId, getIdFromStage, getIdFromPipeline, getLabelFromPipeline, getLabelFromStage, getObjectId, getPipelineStageId, getProperties, getPropertyValueRequirementsFromStage, getStagesFromPipeline, getStageIsClosed, getSubject } from 'conversations-thread-data/tickets/public/operators';
import { PIPELINE_ID, PIPELINE_STAGE_ID } from 'conversations-thread-data/tickets/public/constants';
import { TICKET_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { FAILED, STARTED, SUCCEEDED, UNINITIALIZED } from 'conversations-async-data/async-data/constants/asyncStatuses';
import { getClosedTicket, getUpdatedTicketPipeline, getUpdatedTicketStage } from '../../../usage-tracking/ticketInteraction';
import RequiredFieldsModal from './RequiredFieldsModal';
import ChangePipelineModal from './ChangePipelineModal';
import UITruncateString from 'UIComponents/text/UITruncateString';
import { toTicket } from 'conversations-thread-data/tickets/public/utils';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIFlex from 'UIComponents/layout/UIFlex';
// @ts-expect-error type not available in wootric-nps
import { setupCsat } from 'wootric-nps/csat/csat-setup';
import TicketPipelineStageV2 from './TicketPipelineStageV2';

// eslint-disable-next-line react/display-name
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ValueComponent = props => /*#__PURE__*/_jsx(UITruncateString, Object.assign({
  maxWidth: 150
}, props));
const TicketStageDropdown = ({
  ticket,
  ticketPipelines = [],
  ticketStages = [],
  ticketStageUpdated,
  updateTicketProperties,
  trackEvent,
  ungatedToTicketStatusConditionalProperties,
  isHelpDesk = false
}) => {
  const [pipelineChangeStatus, setPipelineChangeStatus] = useState(UNINITIALIZED);
  const [shouldShowChangePipelineModal, setShouldShowChangePipelineModal] = useState(false);
  const [shouldShowRequiredValuesModal, setShouldShowRequiredValuesModal] = useState(false);
  const [stageChangeStatus, setStageChangeStatus] = useState(UNINITIALIZED);
  const [toPipeline, setToPipeline] = useState();
  const [toStage, setToStage] = useState();
  const dispatch = useDispatch();
  const currentPipelineId = getHsPipelineId(ticket);
  const currentPipeline = ticketPipelines.find(pipeline => getIdFromPipeline(pipeline) === currentPipelineId);
  const stageOptions = ticketStages ? ticketStages.map(stage => ({
    text: getLabelFromStage(stage),
    value: getIdFromStage(stage)
  })) : [];
  const onClickChangePipelineButton = useCallback(() => {
    const pipelineStageId = getPipelineStageId(ticket);
    setShouldShowChangePipelineModal(true);
    setToPipeline(currentPipeline);
    setToStage(ticketStages.find(stage => getIdFromStage(stage) === pipelineStageId));
  }, [currentPipeline, ticket, ticketStages]);
  const onConfirmTicketStage = useCallback(({
    target: {
      value: newStageId
    }
  }) => {
    const newStage = ticketStages.find(ticketStage => getIdFromStage(ticketStage) === newStageId);
    dispatch(ticketStageUpdated());
    const propertyValueRequirements = newStage && getPropertyValueRequirementsFromStage(newStage);
    if (propertyValueRequirements && propertyValueRequirements.length > 0) {
      setShouldShowRequiredValuesModal(true);
      setToStage(newStage);
    } else {
      updateTicketProperties({
        onError: () => setToStage(null),
        onSuccess: () => setToStage(null),
        properties: [{
          name: PIPELINE_STAGE_ID,
          value: getIdFromStage(newStage)
        }]
      });
      trackEvent(getUpdatedTicketStage({
        stage: getLabelFromStage(newStage)
      }));
      if (getStageIsClosed(newStage)) {
        trackEvent(getClosedTicket({
          stage: getLabelFromStage(newStage)
        }));
        if (isHelpDesk) {
          setupCsat('CSAT_SVH_HELPDESK');
        }
      }
    }
  }, [ticketStages, ticketStageUpdated, updateTicketProperties, dispatch, trackEvent, isHelpDesk]);
  const onConfirmRequiredValues = useCallback(propertyUpdates => {
    dispatch(ticketStageUpdated());
    setStageChangeStatus(STARTED);
    const EXCLUDED_PROPERTIES = [PIPELINE_ID, PIPELINE_STAGE_ID];
    const properties = propertyUpdates.filter(propertyUpdate => !EXCLUDED_PROPERTIES.includes(propertyUpdate.name));
    properties.push({
      name: PIPELINE_STAGE_ID,
      value: toStage && getIdFromStage(toStage)
    });
    if (toPipeline) {
      properties.push({
        name: PIPELINE_ID,
        value: getIdFromPipeline(toPipeline)
      });
      trackEvent(getUpdatedTicketPipeline({
        pipeline: getLabelFromPipeline(toPipeline)
      }));
    }
    trackEvent(getUpdatedTicketStage({
      stage: getLabelFromStage(toStage)
    }));
    if (getStageIsClosed(toStage)) {
      trackEvent(getClosedTicket({
        stage: getLabelFromStage(toStage)
      }));
    }
    updateTicketProperties({
      onError: () => {
        setShouldShowRequiredValuesModal(false);
        setStageChangeStatus(FAILED);
        setToPipeline(null);
        setToStage(null);
      },
      onSuccess: () => {
        setShouldShowRequiredValuesModal(false);
        setStageChangeStatus(SUCCEEDED);
        setToPipeline(null);
        setToStage(null);
      },
      properties
    });
  }, [ticketStageUpdated, toPipeline, toStage, updateTicketProperties, dispatch, trackEvent]);
  const onRejectRequiredValues = useCallback(() => {
    setShouldShowRequiredValuesModal(false);
    setToPipeline(null);
    setToStage(null);
  }, []);
  const onChangeTicketPipeline = useCallback(({
    target: {
      value: newPipelineId
    }
  }) => {
    const newPipeline = ticketPipelines.find(pipeline => getIdFromPipeline(pipeline) === newPipelineId);
    const stages = getStagesFromPipeline(newPipeline);
    const newStage = stages && stages.length ? stages[0] : null;
    setToPipeline(newPipeline);
    setToStage(newStage);
  }, [ticketPipelines]);
  const onChangeTicketPipelineStage = useCallback(({
    target: {
      value: newStageId
    }
  }) => {
    const stages = getStagesFromPipeline(toPipeline);
    setToStage(stages.find(stage => getIdFromStage(stage) === newStageId));
  }, [toPipeline]);
  const onConfirmChangePipeline = useCallback(() => {
    const propertyValueRequirementsFromStage = getPropertyValueRequirementsFromStage(toStage);
    if (propertyValueRequirementsFromStage && propertyValueRequirementsFromStage.length > 0) {
      setShouldShowChangePipelineModal(false);
      setShouldShowRequiredValuesModal(true);
    } else {
      dispatch(ticketStageUpdated());
      setPipelineChangeStatus(STARTED);
      const updates = [{
        name: PIPELINE_ID,
        value: getIdFromPipeline(toPipeline)
      }, {
        name: PIPELINE_STAGE_ID,
        value: getIdFromStage(toStage)
      }];
      trackEvent(getUpdatedTicketPipeline({
        pipeline: getLabelFromPipeline(toPipeline)
      }));
      trackEvent(getUpdatedTicketStage({
        stage: getLabelFromStage(toStage)
      }));
      if (getStageIsClosed(toStage)) {
        trackEvent(getClosedTicket({
          stage: getLabelFromStage(toStage)
        }));
      }
      updateTicketProperties({
        onError: () => {
          setShouldShowChangePipelineModal(false);
          setPipelineChangeStatus(FAILED);
          setToPipeline(null);
          setToStage(null);
        },
        onSuccess: () => {
          setShouldShowChangePipelineModal(false);
          setPipelineChangeStatus(SUCCEEDED);
          setToPipeline(null);
          setToStage(null);
        },
        properties: updates
      });
    }
  }, [ticketStageUpdated, toPipeline, toStage, updateTicketProperties, dispatch, trackEvent]);
  const onRejectChangePipeline = useCallback(() => {
    setShouldShowChangePipelineModal(false);
  }, []);
  const renderTicketStagedDropdown = disabled => {
    return /*#__PURE__*/_jsx(UISelect, {
      className: "p-all-0",
      "data-test-id": "ticket-header-stage-dropdown",
      buttonUse: "transparent",
      buttonSize: "medium",
      disabled: disabled,
      dropdownFooter: ticketPipelines && ticketPipelines.length > 1 ? /*#__PURE__*/_jsx(UIButton, {
        onClick: onClickChangePipelineButton,
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "svh-workspace-ui-lib.ticket-header.ticketStatus.changePipelineButton"
        })
      }) : null,
      menuStyle: {
        maxHeight: 350
      },
      menuWidth: 250,
      onChange: onConfirmTicketStage,
      options: stageOptions,
      value: getPipelineStageId(ticket),
      valueComponent: ValueComponent
    });
  };
  const userCanEditTicket = ticket.userPermissions.currentUserCanEdit;
  return /*#__PURE__*/_jsxs(UIFlex, {
    direction: "column",
    className: "p-all-3",
    align: "baseline",
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "svh-workspace-ui-lib.ticket-header.ticketStatus.title"
    }), /*#__PURE__*/_jsx(UITooltip, {
      disabled: userCanEditTicket,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "svh-workspace-ui-lib.ticket-header.ticketStatus.noPermissions"
      }),
      role: "tooltip",
      children: ungatedToTicketStatusConditionalProperties ? /*#__PURE__*/_jsx(TicketPipelineStageV2, {
        ticket: ticket,
        trackEvent: trackEvent,
        dropdownFooter: ticketPipelines && ticketPipelines.length > 1 ? /*#__PURE__*/_jsx(UIButton, {
          onClick: onClickChangePipelineButton,
          use: "link",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "svh-workspace-ui-lib.ticket-header.ticketStatus.changePipelineButton"
          })
        }) : null
      }) : renderTicketStagedDropdown(!userCanEditTicket)
    }), shouldShowChangePipelineModal && /*#__PURE__*/_jsx(ChangePipelineModal, {
      onChangePipeline: onChangeTicketPipeline,
      onChangeStage: onChangeTicketPipelineStage,
      onConfirm: onConfirmChangePipeline,
      onReject: onRejectChangePipeline,
      pipelineChangeStatus: pipelineChangeStatus,
      pipelineOptions: [...ticketPipelines].sort((p1, p2) => {
        const order1 = getDisplayOrderFromPipeline(p1);
        const order2 = getDisplayOrderFromPipeline(p2);
        return order1 - order2;
      }).map(pl => ({
        text: getLabelFromPipeline(pl),
        value: getIdFromPipeline(pl)
      })),
      stageOptions: getStagesFromPipeline(toPipeline).map(stage => ({
        text: getLabelFromStage(stage),
        value: getIdFromStage(stage)
      })),
      toPipeline: toPipeline,
      toStage: toStage
    }), shouldShowRequiredValuesModal && /*#__PURE__*/_jsx(RequiredFieldsModal, {
      onConfirm: onConfirmRequiredValues,
      onReject: onRejectRequiredValues,
      toStage: toStage,
      objectLabel: getSubject(ticket),
      objectId: getObjectId(ticket),
      objectTypeId: TICKET_TYPE_ID,
      propertyValues: getProperties(toTicket(ticket)),
      stageChangeStatus: stageChangeStatus,
      subject: ImmutableMap(toTicket(ticket))
    })]
  });
};
TicketStageDropdown.displayName = 'TicketStageDropdown';
export default TicketStageDropdown;